/** @param {string[]} parts */
export function cn(...parts) {
	return parts.filter(Boolean).join(' ');
}

/**
 * Envoltorio solo del texto del label (no del icono). `sentence` = primera letra en mayúscula; el resto del texto no se altera.
 * @param {'none' | 'sentence' | 'uppercase'} [labelCase]
 * @returns {string | undefined} `undefined` si no se añade clase (modo `none`).
 */
export function getLabelWrapperClassName(labelCase = 'sentence') {
	if (labelCase === 'uppercase') return 'inline min-w-0 uppercase';
	if (labelCase === 'sentence') return 'inline min-w-0 first-letter:uppercase';
	return undefined;
}

const base =
	'inline-flex items-center justify-center gap-2 rounded-md font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60';

export const buttonVariants = {
	primary: 'bg-indigo-600 text-white shadow-sm hover:bg-indigo-700',
	secondary: 'bg-white text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50',
	reject: 'bg-gray-200 text-gray-600 shadow-sm hover:bg-gray-300',
	outline:
		'bg-white text-indigo-600 ring-1 ring-inset ring-indigo-600 hover:bg-indigo-600 hover:text-white hover:ring-indigo-600',
	danger: 'bg-rose-600 text-white shadow-sm hover:bg-rose-500',
	dangerGradient:
		'bg-gradient-to-r from-rose-500 to-rose-600 text-white shadow-lg shadow-rose-500/30 hover:from-rose-600 hover:to-rose-700 disabled:shadow-none',
	success: 'bg-emerald-600 text-white shadow-sm hover:bg-emerald-700',
	warning: 'bg-yellow-500 text-white shadow-sm hover:bg-yellow-700',
	ghost: 'bg-transparent text-indigo-600 hover:bg-indigo-50 shadow-none ring-0',
	/** Acciones secundarias de barra superior en tablas (filtros/exportar). */
	barFilterSecondary:
		'bg-white px-4 py-2 text-md text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300/80 hover:bg-gray-50 hover:text-gray-900 hover:shadow-md hover:ring-gray-400 active:translate-y-0 active:bg-gray-100 focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:!opacity-100 disabled:cursor-not-allowed disabled:translate-y-0 disabled:bg-gray-100 disabled:text-gray-400 disabled:ring-gray-200 disabled:shadow-none disabled:hover:bg-gray-100 disabled:hover:text-gray-400 disabled:hover:shadow-none disabled:hover:ring-gray-200',
	/** Acciones primarias de barra superior en tablas (crear/agregar). */
	barFilterPrimary:
		'bg-indigo-600 px-4 py-2 text-md text-white shadow-sm ring-1 ring-inset ring-indigo-700/30 hover:bg-indigo-700 hover:shadow-md  hover:ring-indigo-700/40 active:translate-y-0 active:bg-indigo-800 focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:!opacity-100 disabled:cursor-not-allowed disabled:translate-y-0 disabled:bg-indigo-300 disabled:text-white/90 disabled:ring-indigo-300/60 disabled:shadow-none disabled:hover:bg-indigo-300 disabled:hover:shadow-none disabled:hover:ring-indigo-300/60',
	/**
	 * Acciones en cabeceras de tabs/drawer (mismo aspecto que «Editar» en TabUserData).
	 */
	drawerActions:
		'!rounded-lg bg-indigo-50 px-4 py-2 text-sm font-semibold text-indigo-600 shadow-none ring-0 hover:bg-indigo-100 transition-colors',
	/** Mismo aspecto que el submit de FormLogin (degradado + sombra + hover) */
	gradient:
		'group relative overflow-hidden inline-flex items-center justify-center gap-2 rounded-xl py-4 px-5 text-base font-semibold text-white bg-gradient-to-r from-indigo-600 to-purple-600 shadow-lg shadow-indigo-500/50 hover:shadow-xl hover:shadow-indigo-600/50 transform transition-all duration-300 hover:-translate-y-0.5 focus:outline-none focus:ring-4 focus:ring-indigo-300 disabled:cursor-not-allowed disabled:opacity-60 disabled:transform-none disabled:shadow-md',
};

/** CTA «Crear una cuenta»: borde = degradado (envoltorio p-[2px]) + interior blanco que en hover funde con el mismo degradado que el login */
export const outlineCtaOuterClassNames =
	'group inline-flex w-full rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 p-[2px] no-underline shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-indigo-600/50 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-indigo-300 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-60';

/** py/px compensan el `p-[2px]` del outer para alinear la caja con `variant="gradient"` (py-4 px-5). */
export const outlineCtaInnerClassNames =
	'flex w-full min-h-0 items-center justify-center gap-2 rounded-[calc(0.75rem-2px)] bg-white py-3.5 px-[1.125rem] text-center text-base font-semibold text-indigo-600 transition-all duration-300 ease-out group-hover:bg-transparent group-hover:text-white';

export const buttonSizes = {
	sm: 'px-3 py-2 text-xs',
	md: 'px-4 py-3 text-sm',
	lg: 'px-5 py-3.5 text-base',
};

/**
 * @param {{ variant?: keyof typeof buttonVariants; size?: keyof typeof buttonSizes; fullWidth?: boolean; className?: string }} opts
 */
export function getButtonClassNames({
	variant = 'primary',
	size = 'md',
	fullWidth = false,
	className = '',
} = {}) {
	if (variant === 'gradient') {
		return cn(
			buttonVariants.gradient,
			fullWidth && 'w-full',
			className,
		);
	}
	if (variant === 'drawerActions') {
		return cn(
			base,
			buttonVariants.drawerActions,
			fullWidth && 'w-full',
			className,
		);
	}
	if (variant === 'barFilterSecondary' || variant === 'barFilterPrimary') {
		return cn(
			base,
			buttonVariants[variant],
			fullWidth && 'w-full',
			className,
		);
	}

	return cn(
		base,
		buttonVariants[variant] || buttonVariants.primary,
		buttonSizes[size] || buttonSizes.md,
		fullWidth && 'w-full',
		className,
	);
}

/** Tamaños para botones solo icono (hit area cuadrada, sin padding de texto). */
export const iconButtonSizes = {
	sm: 'h-8 w-8 min-h-8 min-w-8 shrink-0 p-0',
	md: 'h-9 w-9 min-h-[2.25rem] min-w-[2.25rem] shrink-0 p-0',
	lg: 'h-11 w-11 min-h-[2.75rem] min-w-[2.75rem] shrink-0 p-0',
};

/**
 * Clases para botón cuadrado solo icono (reutiliza `buttonVariants`).
 * `outlineCta` usa el estilo `outline`: el CTA con degradado está pensado para ancho completo.
 * @param {{ variant?: keyof typeof buttonVariants; size?: keyof typeof iconButtonSizes; fullWidth?: boolean; className?: string }} opts
 */
export function getIconButtonClassNames({
	variant = 'ghost',
	size = 'md',
	fullWidth = false,
	className = '',
} = {}) {
	const sizeClass = iconButtonSizes[size] || iconButtonSizes.md;

	if (variant === 'outlineCta') {
		return cn(
			base,
			buttonVariants.outline,
			sizeClass,
			fullWidth && 'w-full',
			className,
		);
	}

	if (variant === 'drawerActions') {
		return cn(
			base,
			'!rounded-lg bg-indigo-50 text-indigo-600 shadow-none ring-0 hover:bg-indigo-100 transition-colors',
			sizeClass,
			fullWidth && 'w-full',
			className,
		);
	}

	if (variant === 'gradient') {
		return cn(
			'group relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-xl font-semibold text-white bg-gradient-to-r from-indigo-600 to-purple-600 shadow-lg shadow-indigo-500/50 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60',
			sizeClass,
			fullWidth && 'w-full',
			className,
		);
	}

	return cn(
		base,
		buttonVariants[variant] || buttonVariants.primary,
		sizeClass,
		fullWidth && 'w-full',
		className,
	);
}
